import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Copy, Check, Save, Edit, Trash } from "lucide-react";

interface SMSSettings {
  id: string;
  phone_number_id: string;
  sms_enabled: boolean;
  auto_reply_enabled: boolean;
  auto_reply_template_id: string | null;
  phone_numbers: {
    display_name: string;
    twilio_phone_number: string;
  };
}

interface Template {
  id: string;
  name: string;
  message_body: string;
  county: string | null;
  is_default: boolean;
}

export function SMSSettingsManager() {
  const [settings, setSettings] = useState<SMSSettings[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [newTemplate, setNewTemplate] = useState({ name: "", message_body: "", county: "" });
  const [copied, setCopied] = useState(false);

  const webhookUrl = `https://ltugnhpocjzhouvvciek.supabase.co/functions/v1/twilio-sms-webhook`;

  useEffect(() => {
    loadSettings();
    loadTemplates();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_settings')
        .select(`
          *,
          phone_numbers (
            display_name,
            twilio_phone_number
          )
        `);

      if (error) throw error;
      setSettings(data || []);
    } catch (error) {
      console.error('Error loading SMS settings:', error);
      toast.error('Failed to load SMS settings');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_templates')
        .select('*')
        .order('is_default', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    }
  };

  const updateSetting = async (id: string, updates: Partial<SMSSettings>) => {
    try {
      const { error } = await supabase
        .from('sms_settings')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      toast.success('Settings updated');
      loadSettings();
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const saveTemplate = async () => {
    try {
      if (editingTemplate) {
        const template = templates.find(t => t.id === editingTemplate);
        if (!template) return;

        const { error } = await supabase
          .from('sms_templates')
          .update({
            name: newTemplate.name,
            message_body: newTemplate.message_body,
            county: newTemplate.county || null
          })
          .eq('id', editingTemplate);

        if (error) throw error;
        toast.success('Template updated');
      } else {
        const { error } = await supabase
          .from('sms_templates')
          .insert({
            name: newTemplate.name,
            message_body: newTemplate.message_body,
            county: newTemplate.county || null
          });

        if (error) throw error;
        toast.success('Template created');
      }

      setNewTemplate({ name: "", message_body: "", county: "" });
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const { error } = await supabase
        .from('sms_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success('Webhook URL copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div>Loading SMS settings...</div>;
  }

  if (settings.length === 0) {
    return (
      <div className="container mx-auto p-4 md:p-6 max-w-7xl">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-lg text-muted-foreground mb-2">
              No SMS settings found
            </p>
            <p className="text-sm text-muted-foreground">
              SMS settings are automatically created when you add phone numbers. 
              Go to Phone Numbers page to add a phone number first.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">SMS Settings</h1>
        <p className="text-sm md:text-base text-muted-foreground">
          Configure SMS auto-reply settings and templates for each phone number
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg md:text-xl">Twilio SMS Webhook URL</CardTitle>
          <CardDescription className="text-sm">
            Configure this URL in your Twilio phone number settings for SMS messages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={webhookUrl}
              readOnly
              className="font-mono text-xs sm:text-sm flex-1"
            />
            <Button onClick={copyWebhookUrl} variant="outline" className="w-full sm:w-auto">
              {copied ? (
                <>
                  <Check className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Copy</span>
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="settings">Phone Settings</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-4">
          {settings.map((setting) => (
            <Card key={setting.id}>
              <CardHeader className="pb-3">
                <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-base md:text-lg">{setting.phone_numbers.display_name}</span>
                  <span className="text-xs sm:text-sm font-mono text-muted-foreground">
                    {setting.phone_numbers.twilio_phone_number}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div className="space-y-0.5">
                    <Label htmlFor={`sms-${setting.id}`} className="text-sm font-medium">
                      Enable SMS
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Allow incoming and outgoing SMS messages
                    </p>
                  </div>
                  <Switch
                    id={`sms-${setting.id}`}
                    checked={setting.sms_enabled}
                    onCheckedChange={(checked) => 
                      updateSetting(setting.id, { sms_enabled: checked })
                    }
                  />
                </div>

                {setting.sms_enabled && (
                  <>
                    <Separator />
                    <div className="flex items-center justify-between py-2">
                      <div className="space-y-0.5">
                        <Label htmlFor={`auto-reply-${setting.id}`} className="text-sm font-medium">
                          Enable Auto-Reply
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Send automatic response to new messages
                        </p>
                      </div>
                      <Switch
                        id={`auto-reply-${setting.id}`}
                        checked={setting.auto_reply_enabled}
                        onCheckedChange={(checked) => 
                          updateSetting(setting.id, { auto_reply_enabled: checked })
                        }
                      />
                    </div>

                    {setting.auto_reply_enabled && (
                      <>
                        <Separator />
                        <div className="space-y-2">
                          <Label htmlFor={`template-${setting.id}`} className="text-sm font-medium">
                            Auto-Reply Template
                          </Label>
                          <Select
                            value={setting.auto_reply_template_id || ""}
                            onValueChange={(value) => 
                              updateSetting(setting.id, { 
                                auto_reply_template_id: value || null 
                              })
                            }
                          >
                            <SelectTrigger id={`template-${setting.id}`} className="w-full">
                              <SelectValue placeholder="Select a template" />
                            </SelectTrigger>
                            <SelectContent>
                              {templates.map((template) => (
                                <SelectItem key={template.id} value={template.id}>
                                  {template.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </CardTitle>
              <CardDescription className="text-sm">
                {editingTemplate 
                  ? 'Update the template details' 
                  : 'Create a new SMS auto-reply template'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template-name" className="text-sm font-medium">
                  Template Name
                </Label>
                <Input
                  id="template-name"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="e.g., Default Auto-Reply"
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="template-body" className="text-sm font-medium">
                  Message Body
                </Label>
                <Textarea
                  id="template-body"
                  value={newTemplate.message_body}
                  onChange={(e) => setNewTemplate({ ...newTemplate, message_body: e.target.value })}
                  placeholder="Thank you for contacting us! We'll respond shortly."
                  rows={4}
                  className="w-full resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  This message will be sent automatically to new SMS conversations
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="template-county" className="text-sm font-medium">
                  County (Optional)
                </Label>
                <Input
                  id="template-county"
                  value={newTemplate.county || ''}
                  onChange={(e) => setNewTemplate({ ...newTemplate, county: e.target.value || '' })}
                  placeholder="e.g., Suffolk"
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank for a general template, or specify for county-specific responses
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  onClick={saveTemplate} 
                  disabled={!newTemplate.name || !newTemplate.message_body}
                  className="w-full sm:w-auto"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </Button>
                {editingTemplate && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingTemplate(null);
                      setNewTemplate({ name: '', message_body: '', county: '' });
                    }}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4">
            {templates.map((template) => (
              <Card key={template.id}>
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 space-y-1">
                      <CardTitle className="text-base md:text-lg flex items-center gap-2 flex-wrap">
                        {template.name}
                        {template.is_default && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-normal">
                            Default
                          </span>
                        )}
                      </CardTitle>
                      {template.county && (
                        <CardDescription className="text-xs">
                          County: {template.county}
                        </CardDescription>
                      )}
                    </div>
                    {!template.is_default && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingTemplate(template.id);
                            setNewTemplate({
                              name: template.name,
                              message_body: template.message_body,
                              county: template.county || '',
                            });
                          }}
                          className="h-8"
                        >
                          <Edit className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteTemplate(template.id)}
                          className="h-8"
                        >
                          <Trash className="h-3 w-3 sm:mr-1" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                    {template.message_body}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}